import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Modal from './common/Modal';
import Loader from './common/Loader';
import { useSettings } from '../context/SettingsContext';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { exportToExcel } from '../utils/exportHelper';

const MySwal = withReactContent(Swal);

const SuperAdminDashboard = () => {
    const navigate = useNavigate();
    const { settings } = useSettings();
    const [stats, setStats] = useState({ schools: 0, users: 0, active_schools: 0 });
    const [schools, setSchools] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('schools');
    const [migrationRunning, setMigrationRunning] = useState(false);
    const [migrationOutput, setMigrationOutput] = useState(null);

    // School Form
    const [showSchoolModal, setShowSchoolModal] = useState(false);
    const [editingSchoolId, setEditingSchoolId] = useState(null); // ID of school being edited
    const [schoolForm, setSchoolForm] = useState({
        name: '', type: 'academy', admin_name: '', email: '', password: '',
        max_students: 100, max_staff: 5, max_courses: 12
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const token = localStorage.getItem('token');
            const config = { headers: { Authorization: `Bearer ${token}` } };

            const statsRes = await axios.get(`${process.env.NODE_ENV === "development" ? "http://localhost:8000" : "https://lms.appy.pk"}/api/super-admin/stats`, config);
            setStats(statsRes.data);

            const schoolsRes = await axios.get(`${process.env.NODE_ENV === "development" ? "http://localhost:8000" : "https://lms.appy.pk"}/api/super-admin/schools`, config);
            setSchools(schoolsRes.data);

            const usersRes = await axios.get(`${process.env.NODE_ENV === "development" ? "http://localhost:8000" : "https://lms.appy.pk"}/api/super-admin/users`, config);
            setUsers(usersRes.data);

            setLoading(false);
        } catch (error) {
            console.error("Error fetching super admin data", error);
            if (error.response && (error.response.status === 403 || error.response.status === 401)) {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                navigate('/login');
            }
            setLoading(false);
        }
    };
    
    const handleExport = () => {
        if (activeTab === 'schools') {
            if (schools.length === 0) {
                MySwal.fire('No Data', 'No schools to export.', 'info');
                return;
            }
            const exportData = schools.map(s => ({
                'School Name': s.name,
                'Type': s.type,
                'Max Students': s.max_students,
                'Max Staff': s.max_staff,
                'Max Courses': s.max_courses,
                'Status': (s.is_active || s.status === 'active') ? 'Active' : 'Inactive',
                'Created At': new Date(s.created_at).toLocaleDateString()
            }));
            exportToExcel(exportData, `Schools_List_${new Date().toISOString().split('T')[0]}`);
        } else {
            if (users.length === 0) {
                MySwal.fire('No Data', 'No users to export.', 'info');
                return;
            }
            const exportData = users.map(u => ({
                'Full Name': u.name,
                'Email': u.email,
                'Role': u.role,
                'School': u.school?.name || 'N/A',
                'Designation': u.designation || '-',
                'Phone': u.phone || '-'
            }));
            exportToExcel(exportData, `Users_List_${new Date().toISOString().split('T')[0]}`);
        }
    };


    const handleSubmitSchool = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('token');
            const config = { headers: { Authorization: `Bearer ${token}` } };

            if (editingSchoolId) {
                // Update implementation
                // Filter only editable fields for update to avoid sending unnecessary data
                const updateData = {
                    name: schoolForm.name,
                    max_students: schoolForm.max_students,
                    max_staff: schoolForm.max_staff,
                    max_courses: schoolForm.max_courses
                };
                await axios.put(`${process.env.NODE_ENV === "development" ? "http://localhost:8000" : "https://lms.appy.pk"}/api/super-admin/schools/${editingSchoolId}`, updateData, config);
            } else {
                // Create implementation
                await axios.post(`${process.env.NODE_ENV === "development" ? "http://localhost:8000" : "https://lms.appy.pk"}/api/super-admin/schools`, schoolForm, config);
            }

            setShowSchoolModal(false);
            setSchoolForm({
                name: '', admin_name: '', email: '', password: '',
                max_students: 100, max_staff: 5, max_courses: 12
            });
            const wasEditing = editingSchoolId; // Capture before nulling
            setEditingSchoolId(null);
            fetchData();

            MySwal.fire({
                icon: 'success',
                title: wasEditing ? 'Success!' : 'Created!',
                text: wasEditing ? 'School limits updated successfully.' : 'New school created successfully.',
                timer: 2000,
                showConfirmButton: false
            });
        } catch (error) {
            console.error("Error saving school", error);
            MySwal.fire({
                icon: 'error',
                title: 'Operation Failed',
                text: error.response?.data?.message || 'Failed to save changes. Please try again.'
            });
        }
    };

    const handleEditClick = (school) => {
        setEditingSchoolId(school.id);
        setSchoolForm({
            name: school.name || '',
            admin_name: '', // We don't edit admin name/email/pass here typically
            email: '',
            password: '',
            max_students: school.max_students || 100,
            max_staff: school.max_staff || 10,
            max_courses: school.max_courses || 5
        });
        setShowSchoolModal(true);
    };

    const handleNewSchoolClick = () => {
        setEditingSchoolId(null);
        setSchoolForm({
            name: '', admin_name: '', email: '', password: '',
            max_students: 100, max_staff: 5, max_courses: 12
        });
        setShowSchoolModal(true);
    };

    const toggleSchoolStatus = async (id) => {
        const result = await MySwal.fire({
            title: 'Are you sure?',
            text: "Do you want to change this school's status?",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, change it!'
        });

        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem('token');
                await axios.post(`${process.env.NODE_ENV === "development" ? "http://localhost:8000" : "https://lms.appy.pk"}/api/super-admin/schools/${id}/toggle`, {}, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                fetchData();
                MySwal.fire(
                    'Updated!',
                    'School status has been updated.',
                    'success'
                );
            } catch (error) {
                console.error("Error toggling school status", error);
                MySwal.fire(
                    'Error!',
                    'Failed to update school status.',
                    'error'
                );
            }
        }
    };

    const toggleUserBlock = async (id) => {
        const result = await MySwal.fire({
            title: 'Are you sure?',
            text: "Do you want to block/unblock this user?",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, do it!'
        });

        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem('token');
                await axios.post(`${process.env.NODE_ENV === "development" ? "http://localhost:8000" : "https://lms.appy.pk"}/api/super-admin/users/${id}/toggle-block`, {}, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                fetchData();
                MySwal.fire(
                    'Updated!',
                    'User status has been updated.',
                    'success'
                );
            } catch (error) {
                console.error("Error toggling user block", error);
                MySwal.fire(
                    'Error!',
                    'Failed to update user status.',
                    'error'
                );
            }
        }
    };

    const handleLoginAs = async (userId) => {
        const result = await MySwal.fire({
            title: 'Login As User?',
            text: "You will be logged out of Super Admin and logged in as this user.",
            icon: 'info',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, Login As User'
        });

        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem('token');
                const currentUser = localStorage.getItem('user');

                // Store Super Admin token and user details before switching
                localStorage.setItem('superAdminToken', token);
                localStorage.setItem('superAdminUser', currentUser);

                const response = await axios.post(`${process.env.NODE_ENV === "development" ? "http://localhost:8000" : "https://lms.appy.pk"}/api/super-admin/users/${userId}/login-as`, {}, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                // Set new token and user
                localStorage.setItem('token', response.data.access_token);
                localStorage.setItem('user', JSON.stringify(response.data.user));

                // Redirect to dashboard
                window.location.href = '/';
            } catch (error) {
                console.error("Error logging in as user", error);
                MySwal.fire(
                    'Login Failed',
                    'Could not login as user. Please try again.',
                    'error'
                );
            }
        }
    };

    const handleDeleteUser = async (userId) => {
        const { value: password } = await MySwal.fire({
            title: 'Delete User & School Data',
            text: "This action is irreversible. It will delete the user, their school, and all associated data. Enter your super admin password to confirm.",
            input: 'password',
            inputPlaceholder: 'Enter your password',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete everything!'
        });

        if (password) {
            try {
                const token = localStorage.getItem('token');
                await axios.delete(`${process.env.NODE_ENV === "development" ? "http://localhost:8000" : "https://lms.appy.pk"}/api/super-admin/users/${userId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                    data: { password }
                });
                fetchData();
                MySwal.fire(
                    'Deleted!',
                    'User and all associated data have been deleted.',
                    'success'
                );
            } catch (error) {
                console.error("Error deleting user", error);
                MySwal.fire(
                    'Error!',
                    error.response?.data?.message || 'Failed to delete user. Please check your password and try again.',
                    'error'
                );
            }
        }
    };

    const handleRunMigrations = async () => {
        const result = await MySwal.fire({
            title: '⚠️ Run Database Migrations?',
            html: `
                <p>This will run any <strong>new pending migrations</strong> on the production database.</p>
                <div class="alert alert-success text-start mt-3 mb-0" style="font-size:0.85rem">
                    ✅ <strong>Safe:</strong> Only adds new columns/tables.<br/>
                    ✅ Old data will <strong>NOT</strong> be deleted.<br/>
                    ✅ Uses <code>--force</code> flag (production-safe).
                </div>
            `,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#7367F0',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, Run Migrations!',
            cancelButtonText: 'Cancel'
        });

        if (!result.isConfirmed) return;

        setMigrationRunning(true);
        setMigrationOutput(null);

        try {
            const token = localStorage.getItem('token');
            const BASE = process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : 'https://lms.appy.pk';
            const response = await axios.post(`${BASE}/api/super-admin/run-migrations`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setMigrationOutput({ success: true, message: response.data.message, output: response.data.output });
            MySwal.fire({ icon: 'success', title: 'Success!', text: response.data.message, timer: 3000 });
        } catch (error) {
            const msg = error.response?.data?.message || 'Migration failed. Check server logs.';
            setMigrationOutput({ success: false, message: msg, output: '' });
            MySwal.fire({ icon: 'error', title: 'Migration Failed', text: msg });
        } finally {
            setMigrationRunning(false);
        }
    };

    if (loading) return <Loader message="Loading Super Admin Dashboard..." />;

    return (
        <div className="container-fluid p-0">
            <div className="section-header mb-4 d-flex justify-content-between align-items-start flex-wrap-mobile">
                <div>
                    <h2 className="fw-bold text-primary">Super Admin</h2>
                    <p className="text-muted">Manage all schools and accounts</p>
                </div>
                <div className="d-flex gap-2">
                    <button className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill px-4 shadow-sm" onClick={handleExport}>
                        <i className="bi bi-file-earmark-spreadsheet"></i> Export
                    </button>
                    <button className="btn btn-primary d-flex align-items-center gap-2 rounded-pill px-4 shadow-sm" onClick={handleNewSchoolClick}>
                        <i className="bi bi-building-add"></i> New School
                    </button>
                </div>
            </div>

            {/* Stats */}
            <div className="row g-4 mb-5">
                <div className="col-md-4">
                    <div className="stat-card">
                        <div className="stat-info">
                            <p className="text-muted mb-1 small fw-bold">Total Schools</p>
                            <h3 className="mb-0">{stats.schools}</h3>
                        </div>
                        <div className="stat-icon bg-primary text-white shadow-sm rounded-circle d-flex align-items-center justify-content-center" style={{ width: 50, height: 50 }}>
                            <i className="bi bi-buildings"></i>
                        </div>
                    </div>
                </div>
                <div className="col-md-4">
                    <div className="stat-card">
                        <div className="stat-info">
                            <p className="text-muted mb-1 small fw-bold">Active Schools</p>
                            <h3 className="mb-0 text-success">{stats.active_schools}</h3>
                        </div>
                        <div className="stat-icon bg-success text-white shadow-sm rounded-circle d-flex align-items-center justify-content-center" style={{ width: 50, height: 50 }}>
                            <i className="bi bi-check-circle"></i>
                        </div>
                    </div>
                </div>
                <div className="col-md-4">
                    <div className="stat-card">
                        <div className="stat-info">
                            <p className="text-muted mb-1 small fw-bold">Total Admins</p>
                            <h3 className="mb-0">{stats.users}</h3>
                        </div>
                        <div className="stat-icon bg-info text-white shadow-sm rounded-circle d-flex align-items-center justify-content-center" style={{ width: 50, height: 50 }}>
                            <i className="bi bi-people"></i>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <ul className="nav nav-pills mb-4 gap-2">
                <li className="nav-item">
                    <button
                        className={`nav-link rounded-pill px-4 ${activeTab === 'schools' ? 'active' : ''}`}
                        onClick={() => setActiveTab('schools')}
                    >
                        Schools
                    </button>
                </li>
                <li className="nav-item">
                    <button
                        className={`nav-link rounded-pill px-4 ${activeTab === 'users' ? 'active' : ''}`}
                        onClick={() => setActiveTab('users')}
                    >
                        User Accounts
                    </button>
                </li>
                <li className="nav-item">
                    <button
                        className={`nav-link rounded-pill px-4 ${activeTab === 'system' ? 'active' : ''}`}
                        onClick={() => setActiveTab('system')}
                    >
                        <i className="bi bi-gear-fill me-1"></i> System
                    </button>
                </li>
            </ul>

            {/* Content */}
            <div className="card-box shadow-sm">
                {activeTab === 'schools' ? (
                    <div className="table-responsive">
                        <table className="table table-bordered table-hover align-middle mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th className="ps-4">School Name</th>
                                    <th>Admin Info</th>
                                    <th>Limits (Stu/Stf/Crs)</th>
                                    <th>Status</th>
                                    <th className="text-end pe-4">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {schools.map(school => (
                                    <tr key={school.id}>
                                        <td className="ps-4">
                                            <div className="d-flex align-items-center gap-2">
                                                <div>
                                                    <div className="fw-bold">{school.name}</div>
                                                    <div className="d-flex gap-1 mt-1">
                                                        <span className={`badge ${school.type === 'school' ? 'bg-info' :
                                                            school.type === 'academy' ? 'bg-warning' :
                                                                'bg-success'
                                                            } bg-opacity-10 ${school.type === 'school' ? 'text-info' :
                                                                school.type === 'academy' ? 'text-warning' :
                                                                    'text-success'
                                                            } border ${school.type === 'school' ? 'border-info' :
                                                                school.type === 'academy' ? 'border-warning' :
                                                                    'border-success'
                                                            } border-opacity-25 rounded-pill`} style={{ fontSize: '0.65rem' }}>
                                                            {school.type === 'school' ? '🏫 School' :
                                                                school.type === 'academy' ? '🎓 Academy' :
                                                                    '🏢 Institute'}
                                                        </span>
                                                        {school.package && (
                                                            <span className="badge bg-purple-light text-primary border border-primary-subtle rounded-pill" style={{ fontSize: '0.65rem' }}>
                                                                {school.package.name} Plan
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            {school.users && school.users.length > 0 ? (
                                                <div>
                                                    <div className="fw-semibold">{school.users[0].name}</div>
                                                    <small className="text-muted">{school.users[0].email}</small>
                                                </div>
                                            ) : <span className="text-muted">No Admin</span>}
                                        </td>
                                        <td>
                                            <div className="d-flex flex-column small">
                                                <div><span className="fw-bold text-dark">Students:</span> <span className="text-muted">{school.students_count} / {school.max_students || '∞'}</span></div>
                                                <div><span className="fw-bold text-dark">Staff:</span> <span className="text-muted">{school.staff_count} / {school.max_staff || '∞'}</span></div>
                                                <div><span className="fw-bold text-dark">Courses:</span> <span className="text-muted">{school.courses_count} / {school.max_courses || '∞'}</span></div>
                                            </div>
                                        </td>
                                        <td>
                                            <span className={`badge rounded-pill ${school.status === 'active' ? 'bg-success bg-opacity-10 text-success' : 'bg-danger bg-opacity-10 text-danger'}`}>
                                                {school.status.charAt(0).toUpperCase() + school.status.slice(1)}
                                            </span>
                                        </td>
                                        <td className="text-end pe-4">
                                            <div className="d-flex flex-column align-items-end gap-2">
                                                <button
                                                    className={`btn btn-sm rounded-pill px-3 ${school.status === 'active' ? 'btn-outline-danger' : 'btn-outline-success'}`}
                                                    onClick={() => toggleSchoolStatus(school.id)}
                                                    style={{ minWidth: '110px' }}
                                                >
                                                    {school.status === 'active' ? 'Block' : 'Activate'}
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-primary rounded-pill px-3"
                                                    onClick={() => handleEditClick(school)}
                                                    style={{ minWidth: '110px' }}
                                                >
                                                    Edit
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : activeTab === 'users' ? (
                    <div className="table-responsive">
                        <table className="table table-bordered table-hover align-middle mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th className="ps-4">User</th>
                                    <th>School</th>
                                    <th>Role</th>
                                    <th>Account Status</th>
                                    <th className="text-end pe-4">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map(user => (
                                    <tr key={user.id}>
                                        <td className="ps-4">
                                            <div className="fw-bold">{user.name}</div>
                                            <small className="text-muted">{user.email}</small>
                                        </td>
                                        <td>{user.school?.name || 'N/A'}</td>
                                        <td><span className="badge bg-light text-dark border">{user.role}</span></td>
                                        <td>
                                            <span className={`badge rounded-pill ${!user.is_blocked ? 'bg-success bg-opacity-10 text-success' : 'bg-danger bg-opacity-10 text-danger'}`}>
                                                {!user.is_blocked ? 'ACTIVE' : 'BLOCKED'}
                                            </span>
                                        </td>
                                        <td className="text-end pe-4">
                                            <div className="d-flex flex-column align-items-end gap-2">
                                                <button
                                                    className={`btn btn-sm rounded-pill px-3 ${!user.is_blocked ? 'btn-outline-warning' : 'btn-outline-success'}`}
                                                    onClick={() => toggleUserBlock(user.id)}
                                                    style={{ minWidth: '100px' }}
                                                >
                                                    {!user.is_blocked ? 'Block' : 'Unblock'}
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-primary rounded-pill px-3"
                                                    onClick={() => handleLoginAs(user.id)}
                                                    style={{ minWidth: '100px' }}
                                                >
                                                    Login As
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-danger rounded-pill px-3"
                                                    onClick={() => handleDeleteUser(user.id)}
                                                    style={{ minWidth: '100px' }}
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : activeTab === 'system' ? (
                    <div className="p-4">
                        <h5 className="fw-bold mb-1">Database Migrations</h5>
                        <p className="text-muted small mb-4">Run pending database migrations safely. This will only add new tables/columns and will <strong>never</strong> delete existing data.</p>

                        <div className="card border-0 bg-light rounded-3 p-4 mb-4" style={{ maxWidth: 520 }}>
                            <div className="d-flex align-items-center gap-3 mb-3">
                                <div className="rounded-circle bg-primary bg-opacity-10 text-primary d-flex align-items-center justify-content-center" style={{ width: 48, height: 48 }}>
                                    <i className="bi bi-database-gear fs-5"></i>
                                </div>
                                <div>
                                    <div className="fw-bold">Run Database Migrations</div>
                                    <div className="text-muted small">Only new pending migrations will be applied</div>
                                </div>
                            </div>

                            <div className="d-flex gap-2 flex-wrap mb-3">
                                <span className="badge bg-success bg-opacity-10 text-success border border-success-subtle rounded-pill px-3">✅ Safe for production</span>
                                <span className="badge bg-info bg-opacity-10 text-info border border-info-subtle rounded-pill px-3">🛡️ No data loss</span>
                                <span className="badge bg-warning bg-opacity-10 text-warning border border-warning-subtle rounded-pill px-3">⚡ Adds new features</span>
                            </div>

                            <button
                                className="btn btn-primary rounded-pill px-4 d-flex align-items-center gap-2"
                                onClick={handleRunMigrations}
                                disabled={migrationRunning}
                            >
                                {migrationRunning ? (
                                    <><span className="spinner-border spinner-border-sm" role="status"></span> Running Migrations...</>
                                ) : (
                                    <><i className="bi bi-play-circle-fill"></i> Run Migrations Now</>
                                )}
                            </button>
                        </div>

                        {migrationOutput && (
                            <div className={`card border-0 rounded-3 p-4 ${migrationOutput.success ? 'bg-success bg-opacity-10' : 'bg-danger bg-opacity-10'}`} style={{ maxWidth: 520 }}>
                                <div className={`fw-bold mb-2 ${migrationOutput.success ? 'text-success' : 'text-danger'}`}>
                                    {migrationOutput.success ? '✅ Migration Successful' : '❌ Migration Failed'}
                                </div>
                                <p className="small mb-2">{migrationOutput.message}</p>
                                {migrationOutput.output && (
                                    <pre className="bg-dark text-light rounded p-3 small mb-0" style={{ maxHeight: 200, overflow: 'auto', fontSize: '0.75rem' }}>{migrationOutput.output}</pre>
                                )}
                            </div>
                        )}
                    </div>
                ) : null}
            </div>

            {/* School Modal */}
            <Modal show={showSchoolModal} onClose={() => setShowSchoolModal(false)} title={editingSchoolId ? "Edit School" : "Create New School"}>
                <form onSubmit={handleSubmitSchool}>
                    <div className="mb-3">
                        <label className="form-label text-muted small fw-bold">School Name</label>
                        <input type="text" className="form-control" value={schoolForm.name} onChange={e => setSchoolForm({ ...schoolForm, name: e.target.value })} required placeholder="e.g. Excellence Academy" />
                    </div>

                    <div className="mb-3">
                        <label className="form-label text-muted small fw-bold">Institution Type</label>
                        <select
                            className="form-select"
                            value={schoolForm.type}
                            onChange={e => setSchoolForm({ ...schoolForm, type: e.target.value })}
                            required
                        >
                            <option value="academy">🎓 Academy (Courses like Web Development, Graphic Design)</option>
                            <option value="school">🏫 School (Classes like Nursery, KG, 1st-10th Grade)</option>
                            <option value="institute">🏢 Institute (Professional Training & Certifications)</option>
                        </select>
                        <small className="text-muted">
                            {schoolForm.type === 'academy' ? '📚 Academies use "Courses" and "Batches"' :
                                schoolForm.type === 'school' ? '🎓 Schools use "Classes" and "Sections"' :
                                    '📖 Institutes use "Courses" and "Batches"'}
                        </small>
                    </div>

                    {!editingSchoolId && (
                        <>
                            <div className="mb-3">
                                <label className="form-label text-muted small fw-bold">Admin Name</label>
                                <input type="text" className="form-control" value={schoolForm.admin_name} onChange={e => setSchoolForm({ ...schoolForm, admin_name: e.target.value })} required placeholder="e.g. John Principal" />
                            </div>
                            <div className="mb-3">
                                <label className="form-label text-muted small fw-bold">Admin Email</label>
                                <input type="email" className="form-control" value={schoolForm.email} onChange={e => setSchoolForm({ ...schoolForm, email: e.target.value })} required placeholder="admin@school.com" />
                            </div>
                            <div className="mb-3">
                                <label className="form-label text-muted small fw-bold">Password</label>
                                <input type="password" className="form-control" value={schoolForm.password} onChange={e => setSchoolForm({ ...schoolForm, password: e.target.value })} required minLength="6" />
                            </div>
                        </>
                    )}

                    <div className="row">
                        <div className="col-md-4 mb-3">
                            <label className="form-label text-muted small fw-bold">Max Students</label>
                            <input type="number" className="form-control" value={schoolForm.max_students} onChange={e => setSchoolForm({ ...schoolForm, max_students: e.target.value })} />
                        </div>
                        <div className="col-md-4 mb-3">
                            <label className="form-label text-muted small fw-bold">Max Staff</label>
                            <input type="number" className="form-control" value={schoolForm.max_staff} onChange={e => setSchoolForm({ ...schoolForm, max_staff: e.target.value })} />
                        </div>
                        <div className="col-md-4 mb-3">
                            <label className="form-label text-muted small fw-bold">Max Courses</label>
                            <input type="number" className="form-control" value={schoolForm.max_courses} onChange={e => setSchoolForm({ ...schoolForm, max_courses: e.target.value })} />
                        </div>
                    </div>

                    <div className="mb-4">
                        <label className="form-label text-muted small fw-bold d-block mb-3">Quick Apply Package</label>
                        <div className="row g-3">
                            {[
                                { name: 'Free', color: 'secondary', icon: 'bi-box', students: 100, staff: 5, courses: 12 },
                                { name: 'Basic', color: 'primary', icon: 'bi-gem', students: 500, staff: 20, courses: 50 },
                                { name: 'Pro', color: 'info', icon: 'bi-star', students: 1000, staff: 50, courses: 100 },
                                { name: 'Premium', color: 'warning', icon: 'bi-trophy', students: 10000, staff: 200, courses: 200 }
                            ].map((pkg) => {
                                const isActive = Number(schoolForm.max_students) === pkg.students &&
                                    Number(schoolForm.max_staff) === pkg.staff &&
                                    Number(schoolForm.max_courses) === pkg.courses;
                                return (
                                    <div className="col-6" key={pkg.name}>
                                        <div
                                            className={`card h-100 border text-center ${isActive ? `bg-${pkg.color} bg-opacity-10 shadow-sm` : ''}`}
                                            style={{
                                                cursor: 'pointer',
                                                borderColor: `var(--bs-${pkg.color})`,
                                                borderLeftWidth: isActive ? '8px' : '5px',
                                                transition: 'all 0.2s',
                                                transform: isActive ? 'scale(1.02)' : 'none'
                                            }}
                                            onClick={() => setSchoolForm({ ...schoolForm, max_students: pkg.students, max_staff: pkg.staff, max_courses: pkg.courses })}
                                        >
                                            <div className="card-body p-3 position-relative">
                                                {isActive && (
                                                    <div className={`position-absolute top-0 end-0 me-2 mt-1 text-${pkg.color}`}>
                                                        <i className="bi bi-check-circle-fill"></i>
                                                    </div>
                                                )}
                                                <h6 className={`fw-bold text-${pkg.color} text-uppercase mb-2`}>
                                                    <i className={`bi ${pkg.icon} me-2`}></i>{pkg.name}
                                                </h6>
                                                <div className="d-flex justify-content-center gap-3 small text-secondary">
                                                    <span title="Students"><i className="bi bi-people-fill me-1"></i>{pkg.students >= 1000 ? (pkg.students / 1000) + 'k' : pkg.students}</span>
                                                    <span title="Staff"><i className="bi bi-person-badge-fill me-1"></i>{pkg.staff}</span>
                                                    <span title="Courses"><i className="bi bi-book-fill me-1"></i>{pkg.courses}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>

                    <div className="d-flex justify-content-end gap-2 mt-4">
                        <button type="button" className="btn btn-light px-4 rounded-pill" onClick={() => setShowSchoolModal(false)}>Cancel</button>
                        <button type="submit" className="btn btn-primary px-4 rounded-pill">{editingSchoolId ? "Update School" : "Create School"}</button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default SuperAdminDashboard;
